import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import AuthedImage from '../components/AuthedImage';

// <input type="datetime-local"> wants local "YYYY-MM-DDTHH:mm" with no zone.
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function editFormFrom(task) {
  return {
    title: task.title,
    description: task.description || '',
    assigned_to: String(task.assigned_to),
    start_at: toLocalInput(task.start_at),
    due_at: toLocalInput(task.due_at),
  };
}

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [comment, setComment] = useState('');
  const [completionPhoto, setCompletionPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .getTask(id)
      .then(setTask)
      .catch(() => toast.error("Couldn't load that task."));
  }, [id]);

  useEffect(() => {
    if (user?.role === 'admin') {
      api.listAssignableUsers().then(setUsers).catch(() => {});
    }
  }, [user]);

  function startEditing() {
    setEditForm(editFormFrom(task));
    setEditing(true);
  }

  function setField(field, value) {
    setEditForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSaveEdit(e) {
    e.preventDefault();
    if (!editForm.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    // Only send what changed, so untouched dates aren't rewritten at
    // minute precision (which would re-arm their reminders).
    const original = editFormFrom(task);
    const updates = {};
    if (editForm.title !== original.title) updates.title = editForm.title;
    if (editForm.description !== original.description) updates.description = editForm.description;
    if (editForm.assigned_to !== original.assigned_to) updates.assigned_to = Number(editForm.assigned_to);
    for (const field of ['start_at', 'due_at']) {
      if (editForm[field] !== original[field]) {
        updates[field] = editForm[field] ? new Date(editForm[field]).toISOString() : null;
      }
    }
    if (Object.keys(updates).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      setTask(await api.updateTask(id, updates));
      setEditing(false);
      toast.success('Task updated');
    } catch (err) {
      toast.error(err.message || "Couldn't save the task.");
    } finally {
      setSaving(false);
    }
  }

  async function handleComplete(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      if (comment) formData.append('comment', comment);
      if (completionPhoto) formData.append('photo', completionPhoto);
      const updated = await api.completeTask(id, formData);
      setTask(updated);
      toast.success('Task marked complete');
    } catch {
      toast.error("Couldn't complete the task.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleArchive() {
    setArchiving(true);
    try {
      const updated = task.archived_at
        ? await api.unarchiveTask(id)
        : await api.archiveTask(id);
      setTask(updated);
      toast.success(updated.archived_at ? 'Task archived' : 'Task unarchived');
    } catch (err) {
      toast.error(err.message || "Couldn't update archive status.");
    } finally {
      setArchiving(false);
    }
  }

  async function handleReopen() {
    setReopening(true);
    try {
      setTask(await api.reopenTask(id));
      setComment('');
      setCompletionPhoto(null);
      toast.success('Task reopened');
    } catch (err) {
      toast.error(err.message || "Couldn't reopen the task.");
    } finally {
      setReopening(false);
    }
  }

  if (!task) return <div className="page"><p className="hint">Loading…</p></div>;

  const isAssignee = user?.id === task.assigned_to;
  const isCreator = user?.id === task.created_by;

  return (
    <div className="page">
      <button className="link-button" onClick={() => navigate(-1)}>&larr; Back</button>

      {task.photo_filename && task.photo_scan_status === 'clean' && (
        <AuthedImage url={api.taskPhotoUrl(task.id)} alt="Task" className="task-photo" />
      )}

      <h1>{task.title}</h1>
      {task.description && <p>{task.description}</p>}
      {task.start_at && new Date(task.start_at) > new Date() && (
        <p className="hint">Scheduled to start: {new Date(task.start_at).toLocaleString()}</p>
      )}
      {task.due_at && <p className="hint">Due: {new Date(task.due_at).toLocaleString()}</p>}
      {task.repeat_count > 1 && (
        <p className="hint">Repeats {task.repeat_interval} — {task.repeat_index} of {task.repeat_count}</p>
      )}
      <span className={`badge ${task.status}`}>{task.status}</span>
      {task.archived_at && <span className="badge archived">archived</span>}

      {task.status === 'completed' && (
        <div className="card">
          <strong>Completed</strong>
          <p className="hint">{new Date(task.completed_at).toLocaleString()}</p>
          <strong>Completion comment</strong>
          <p>{task.completion_comment || '(no comment)'}</p>
          {task.completion_photo_filename && task.completion_photo_scan_status === 'clean' && (
            <AuthedImage
              url={api.taskCompletionPhotoUrl(task.id)}
              alt="Completion"
              className="task-photo"
            />
          )}
        </div>
      )}

      {task.status === 'completed' && !task.archived_at && user?.role === 'admin' && (
        <button onClick={handleReopen} disabled={reopening}>
          {reopening ? 'Reopening…' : 'Reopen'}
        </button>
      )}

      {task.status === 'completed' && (isAssignee || isCreator) && (
        <button onClick={handleToggleArchive} disabled={archiving}>
          {archiving ? 'Saving…' : task.archived_at ? 'Unarchive' : 'Archive'}
        </button>
      )}

      {user?.role === 'admin' && !editing && (
        <button onClick={startEditing}>Edit task</button>
      )}

      {user?.role === 'admin' && editing && (
        <form className="card" onSubmit={handleSaveEdit}>
          <h2>Edit task</h2>
          <label>
            Title
            <input value={editForm.title} onChange={(e) => setField('title', e.target.value)} required />
          </label>
          <label>
            Description
            <textarea
              value={editForm.description}
              onChange={(e) => setField('description', e.target.value)}
              rows={3}
            />
          </label>
          <label>
            Assigned to
            <select
              value={editForm.assigned_to}
              onChange={(e) => setField('assigned_to', e.target.value)}
              disabled={task.status !== 'open'}
            >
              {!users.some((u) => String(u.id) === editForm.assigned_to) && (
                <option value={editForm.assigned_to}>(inactive user)</option>
              )}
              {users.map((u) => (
                <option key={u.id} value={String(u.id)}>{u.name}</option>
              ))}
            </select>
          </label>
          {task.status !== 'open' && <p className="hint">Completed tasks can't be reassigned.</p>}
          <label>
            Start date/time (optional)
            <input
              type="datetime-local"
              value={editForm.start_at}
              onChange={(e) => setField('start_at', e.target.value)}
            />
          </label>
          <label>
            Due date/time (optional)
            <input
              type="datetime-local"
              value={editForm.due_at}
              onChange={(e) => setField('due_at', e.target.value)}
            />
          </label>
          <button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
          <button type="button" className="link-button" onClick={() => setEditing(false)} disabled={saving}>
            Cancel
          </button>
        </form>
      )}

      {task.status === 'open' && isAssignee && (
        <form className="card" onSubmit={handleComplete}>
          <h2>Mark this task complete</h2>
          <label>
            Comment (optional)
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} />
          </label>
          <label>
            Photo (optional)
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCompletionPhoto(e.target.files[0] || null)}
            />
          </label>
          <button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Mark complete'}
          </button>
        </form>
      )}
    </div>
  );
}
